"use client";

import Link from "next/link";

import { distanciaTexto } from "@vecipp/core/lib/geo";
import type { ProductoCerca } from "@vecipp/core/lib/tienda-vecipp";
import { cn } from "cn";
import { Plus, ShoppingBasket } from "lucide-react";

import { useCarrito } from "@/lib/carrito";
import { pesos } from "@/lib/formato";

/**
 * **La tarjeta de un producto en la portada, con su `+`.**
 *
 * Es la pieza central del cambio del 2026-09-09: la portada dejó de abrir con una lista de tiendas y
 * abre con **producto**, que es la forma de la referencia (`REFERENCIA-GROSERI/FUNCIONES.md`, ficha de
 * `index.html`). El motivo no es estético: **nadie se levanta queriendo entrar a una tienda, se levanta
 * queriendo pan.** Poner el producto delante quita un paso, y el paso que se quita es el que hace que la
 * gente se vaya.
 *
 * 🔑 **El `+` agrega sin salir de la portada.** Toda la tarjeta es un enlace a la tienda —para ver el
 * resto del catálogo— pero el botón se lleva el clic con `preventDefault`: si abrir la tienda fuera
 * obligatorio para meter un arroz, la portada no serviría de nada.
 *
 * ⚠️ **Lo que la referencia pone aquí y nosotros NO**: `4.2 ★`, `37 mins` y `50% OFF`. No existe ninguno
 * de los tres en la base. En su lugar va **la distancia**, que sí sabemos, y el nombre de la tienda —que
 * en la suya no está, porque allá una tarjeta de producto no dice de quién es, y acá sí importa: se está
 * comprando **al vecino**, no a una cadena.
 */
export function TarjetaProducto({
  producto: p,
  compacta = false,
}: {
  producto: ProductoCerca;
  /** Ancho fijo para la tira que se desliza del inicio; en la rejilla de `/tiendas` manda la columna. */
  compacta?: boolean;
}) {
  const { agregar } = useCarrito();
  const agotado = p.stock !== null && p.stock <= 0;

  return (
    <li className={cn("relative", compacta ? "w-40 shrink-0 md:w-auto" : "")}>
      <Link
        href={`/tienda/${p.id_tienda}`}
        className="flex h-full flex-col overflow-hidden rounded-2xl border bg-card"
      >
        {/* ⚠️ **La foto va `absolute inset-0`, no solo `size-full`.** `height:100%` contra un padre de
            alto automático no resuelve: la imagen cae a su alto intrínseco y **estira el contenedor**,
            ganándole al `aspect-ratio`. Medido el 2026-09-09: una foto de 800×1200 dejó su tarjeta con
            240 px de alto en una fila donde las demás tenían 160, y la tira quedó desalineada. Fuera del
            flujo, manda el aspecto. */}
        <span className="relative block aspect-square w-full bg-muted">
          {p.imagen_url ? (
            // biome-ignore lint/performance/noImgElement: foto de contenido servida por /api/imagen
            <img
              src={p.imagen_url}
              alt=""
              className={`absolute inset-0 size-full object-cover ${agotado ? "opacity-40" : ""}`}
              loading="lazy"
            />
          ) : (
            <span className="flex size-full items-center justify-center">
              <ShoppingBasket className="size-8 text-muted-foreground" aria-hidden="true" />
            </span>
          )}

          {/* La distancia va **sobre la foto, abajo a la derecha**, en el sitio donde la referencia pone
              los minutos de entrega. Mismo lugar, dato honesto. */}
          {/* ⚠️ **Arriba a la izquierda, no abajo a la derecha.** Abajo chocaba con el `+`, que ahora
              vive sobre la foto: dos píldoras en la misma esquina, una encima de la otra. Y así queda
              en el mismo sitio que la ficha de envío de la tarjeta de tienda — dos tarjetas distintas
              que se leen igual. */}
          {p.distancia_km !== null ? (
            <span className="absolute top-2 left-2 rounded-full bg-background/95 px-2 py-1 font-semibold text-[11px] shadow-sm">
              {distanciaTexto(p.distancia_km)}
            </span>
          ) : null}

          {agotado ? (
            <span className="absolute inset-x-0 bottom-0 bg-destructive/90 py-1 text-center font-semibold text-[11px] text-white">
              Agotado
            </span>
          ) : null}
        </span>

        <span className="flex flex-1 flex-col gap-0.5 p-3">
          <span className="line-clamp-2 font-bold text-[15px] leading-snug">{p.nombre}</span>
          <span className="truncate text-muted-foreground text-xs">{p.tienda}</span>
          {/* 🔑 **El precio es lo que se compara**, así que es lo más pesado de la tarjeta después de
              la foto. Antes iba al mismo tamaño que el nombre y las dos líneas competían. */}
          <span className="mt-auto flex items-baseline gap-1.5 pt-1.5">
            <span className="font-bold text-[17px] tabular-nums">{pesos(p.precio)}</span>
            {p.unidad_medida && p.unidad_medida.toLowerCase() !== "unidad" ? (
              <span className="text-muted-foreground text-xs">{p.unidad_medida}</span>
            ) : null}
          </span>
        </span>
      </Link>

      {/* 🔑 **El círculo va sobre la FOTO, no sobre el texto** (2026-09-12). Estaba abajo a la derecha
          de la tarjeta, encima del bloque del precio: había que reservarle un hueco con `pr-12` y aun
          así el nombre se partía antes de tiempo. Sobre la foto no le quita sitio a nada, se ve contra
          la imagen y queda a la misma altura en todas las tarjetas de la fila, que es lo que hace que
          una rejilla se lea como una rejilla.
          ⚠️ **44 px, no 38 como la referencia**: es el mínimo táctil que este proyecto respeta. */}
      {/* ⚠️ **La capa existe porque el botón NO puede ir dentro del enlace.** Un `<button>` dentro de un
          `<a>` es HTML inválido y los lectores de pantalla lo anuncian mal, así que vive fuera, sobre la
          tarjeta. Pero entonces «abajo a la derecha» es abajo de **toda** la tarjeta, no de la foto.
          Esta capa copia el alto de la foto (`aspect-square`, el mismo que ella) y le da al botón una
          esquina de la que colgarse. `pointer-events-none` para no robarle el clic al enlace de abajo. */}
      <span className="pointer-events-none absolute inset-x-0 top-0 aspect-square">
      <button
        type="button"
        disabled={agotado}
        aria-label={`Agregar ${p.nombre} al pedido`}
        onClick={(e) => {
          e.preventDefault();
          agregar(
            { idTienda: p.id_tienda, nombre: p.tienda },
            { idItem: p.id_item, nombre: p.nombre, precio: p.precio, cantidad: 1 },
          );
        }}
        className="pointer-events-auto absolute right-2 bottom-2 inline-flex size-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-background transition hover:brightness-110 active:scale-95 disabled:opacity-40"
      >
        <Plus className="size-5" aria-hidden="true" />
      </button>
      </span>
    </li>
  );
}
